// Date sense-check helpers — produce friendly warnings (not blocking errors)
// when an organisation's start/end dates look implausible against the user's
// date of birth or "now".
//
// Inputs are YYYY-MM (or YYYY-MM-DD) strings; missing inputs are skipped.
// Returns an array of { id, severity, message } objects so the UI can render
// each as a hint chip without re-implementing the comparison logic.

const SEV_WARN = 'warn';
const SEV_INFO = 'info';

function parseYM(value) {
  if (!value) return null;
  const m = String(value).match(/^(\d{4})-(\d{2})/);
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  if (!year || !month || month < 1 || month > 12) return null;
  return { year, month, sortable: year * 100 + month };
}

function nowYM() {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1, sortable: d.getFullYear() * 100 + (d.getMonth() + 1) };
}

/**
 * Returns inline warnings for the date fields against the user's DOB and
 * "now". `dob` is the user's date_of_birth (YYYY-MM-DD or YYYY-MM).
 *
 * Warnings:
 *   - "before-dob-start": start_date predates DOB
 *   - "before-dob-end": end_date predates DOB
 *   - "future-start": start_date is in the future
 *   - "future-end": end_date is in the future (info-only — the org could be ongoing)
 *   - "end-before-start": end_date is before start_date
 *   - "very-young-school": start_date for a school is before the user turns 4
 */
export function senseCheckOrgDates({
  dob,
  startDate,
  endDate,
  type,
} = {}) {
  const warnings = [];
  const dobYm = parseYM(dob);
  const start = parseYM(startDate);
  const end = parseYM(endDate);
  const now = nowYM();

  if (start && end && end.sortable < start.sortable) {
    warnings.push({
      id: 'end-before-start',
      severity: SEV_WARN,
      message: 'The "to" date is before the "from" date — please double-check.',
    });
  }

  if (dobYm) {
    if (start && start.sortable < dobYm.sortable) {
      warnings.push({
        id: 'before-dob-start',
        severity: SEV_WARN,
        message:
          "The 'from' date is before your date of birth — was this perhaps a different year?",
      });
    }
    if (end && end.sortable < dobYm.sortable) {
      warnings.push({
        id: 'before-dob-end',
        severity: SEV_WARN,
        message:
          "The 'to' date is before your date of birth — was this perhaps a different year?",
      });
    }

    // School-specific: starting before the academic year you turn 4 is unusual
    if (type === 'school' && start) {
      // UK school cutoff: child starts Reception in Sept of year DOB.year + 5 if Sept-Dec, else +4.
      const recYear = dobYm.year + (dobYm.month >= 9 ? 5 : 4);
      // Allow nursery from DOB.year + 3, so flag earlier than that
      if (start.year < recYear - 1) {
        warnings.push({
          id: 'very-young-school',
          severity: SEV_INFO,
          message:
            "That's earlier than most UK school start dates — was this nursery, or a different year?",
        });
      }
    }
  }

  if (start && start.sortable > now.sortable) {
    warnings.push({
      id: 'future-start',
      severity: SEV_WARN,
      message: "The 'from' date is in the future — please double-check.",
    });
  }
  if (end && end.sortable > now.sortable) {
    warnings.push({
      id: 'future-end',
      severity: SEV_INFO,
      message:
        "The 'to' date is in the future — leave blank if this is ongoing.",
    });
  }

  return warnings;
}
